import { supabase } from "../../lib/supabaseClient";
import { notifyLikePush, notifyMatchPush } from "./pushNotify";
import { createLikeLimitError, isLikeLimitError } from "../../util/likeLimit";

function requireClient() {
  if (!supabase) throw new Error("Supabase is not configured.");
  return supabase;
}

export async function AddLike(data) {
  const likerId =
    data.likerId ?? data.userId ?? data.fromUserId ?? data.LikerProfileId ?? data.likerProfileId;
  const likedId =
    data.likedId ?? data.likedUserId ?? data.toUserId ?? data.LikedProfileId ?? data.likedProfileId;
  const { data: result, error } = await requireClient().rpc("add_like", {
    p_liker_id: String(likerId),
    p_liked_id: String(likedId),
  });
  if (error) {
    if (isLikeLimitError(error)) throw createLikeLimitError(error);
    throw error;
  }

  const likedUserId = String(likedId);
  const actorId = String(likerId);

  if (result?.isMatch) {
    const conversationId = result.conversationId ?? null;
    // Notify the other user (first liker). Matcher already gets in-app celebration.
    void notifyMatchPush({
      recipientId: likedUserId,
      actorId,
      conversationId,
    });
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("payinapo:conversations-changed", {
          detail: { conversationId, reason: "match" },
        })
      );
    }
  } else if (result?.message === "liked") {
    void notifyLikePush({ recipientId: likedUserId, actorId });
  }

  return result;
}

export async function AddDislike(data) {
  const dislikerId =
    data.dislikerId ?? data.userId ?? data.fromUserId ?? data.dislikerProfileId ?? data.DislikerProfileId;
  const dislikedId =
    data.dislikedId ?? data.dislikedUserId ?? data.toUserId ?? data.dislikedProfileId ?? data.DislikedProfileId;
  const { error } = await requireClient().from("dislikes").insert({
    disliker_id: String(dislikerId),
    disliked_id: String(dislikedId),
  });
  if (error && error.code !== "23505") throw error;
  return { message: "disliked" };
}

/** Clear this user's passes so Discover can loop the pool again. */
export async function ClearMyDislikes(userId) {
  if (!userId) return { success: true };
  const { error } = await requireClient()
    .from("dislikes")
    .delete()
    .eq("disliker_id", String(userId));
  if (error) throw error;
  return { success: true };
}

export async function GetTodayLikeCount(userId) {
  const { data, error } = await requireClient().rpc("today_like_count", {
    p_user_id: String(userId),
  });
  if (error) throw error;
  return data ?? 0;
}

export async function GetAllInterestsByUserId(userId) {
  const { data, error } = await requireClient()
    .from("user_interests")
    .select("interest_id, interests(name)")
    .eq("user_id", userId);
  if (error) throw error;
  return (data || []).map((row) => row.interests?.name).filter(Boolean);
}

function parseInterestPayload(data) {
  const userId = data.userId ?? data.UserId;
  const interestName = data.interest ?? data.name ?? data.InterestName;
  return { userId, interestName };
}

export async function ToggleInterest(data) {
  const { userId, interestName } = parseInterestPayload(data);
  if (!userId || !interestName) throw new Error("userId and interest name required");

  const client = requireClient();

  let { data: interest } = await client.from("interests").select("id").eq("name", interestName).maybeSingle();
  if (!interest) {
    const { data: created, error: createError } = await client
      .from("interests")
      .insert({ name: interestName })
      .select("id")
      .single();
    if (createError) throw createError;
    interest = created;
  }

  const { data: existing } = await client
    .from("user_interests")
    .select("*")
    .eq("user_id", userId)
    .eq("interest_id", interest.id)
    .maybeSingle();

  if (existing) {
    const { error } = await client.from("user_interests").delete().eq("user_id", userId).eq("interest_id", interest.id);
    if (error) throw error;
    return false;
  }

  const { error } = await client.from("user_interests").insert({ user_id: userId, interest_id: interest.id });
  if (error) throw error;
  return true;
}

export async function CheckIfInterestExist(data) {
  const { userId, interestName } = parseInterestPayload(data);
  if (!userId || !interestName) return false;

  const { data: interest } = await requireClient().from("interests").select("id").eq("name", interestName).maybeSingle();
  if (!interest) return false;

  const { data: existing } = await requireClient()
    .from("user_interests")
    .select("*")
    .eq("user_id", userId)
    .eq("interest_id", interest.id)
    .maybeSingle();
  return !!existing;
}

export async function AddMatch() {
  return { message: "handled by add_like RPC" };
}
